import { CUSTOM_ELEMENTS_SCHEMA, inject, isDevMode, provideAppInitializer } from '@angular/core';
import { AppConfig } from './app.config';

import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CommonModule, CurrencyPipe, PercentPipe, DatePipe} from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';

import { NgProgressbar } from 'ngx-progressbar';

import { OverlayContainer } from '@angular/cdk/overlay';
import { CustomOverlayContainer } from './theme/utils/custom-overlay-container';

import { AppRoutingModule } from './app.routing';
import { SharedModule } from './shared/shared.module';
import { InputFileConfig } from './shared/input-file/interfaces/input-file-config';

import { AppComponent } from './app.component';
import { AppSettings } from './app.settings';
import { AppInterceptor } from './theme/utils/app-interceptor';

import { PagesComponent } from './pages/pages.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { ToolbarComponent } from './theme/components/toolbar/toolbar.component';
import { UserMenuComponent } from './theme/components/user-menu/user-menu.component';
import { LangComponent } from './theme/components/lang/lang.component';
import { SocialIconsComponent } from './theme/components/social-icons/social-icons.component';
import { HorizontalMenuComponent } from './theme/components/menu/horizontal-menu/horizontal-menu.component';
import { VerticalMenuComponent } from './theme/components/menu/vertical-menu/vertical-menu.component';

import { SignalRService } from './services/SignalRService';  
import { UserManagementApiService } from './services/UserManagementApiService';
import { GeneralDataApiService } from './services/GeneralDataApiService';
import { PropertyDataApiService } from './services/PropertyDataApiService';
import { AuctionManagementApiService } from './services/AuctionManagementApiService';
import { AuthorizationApiService } from "./services/AuthorizationApiService";
import { AuthenticationService } from './services/AuthenticationService';
import { PropertyService } from "./services/PropertyService";
import { GeneralDataService } from "./services/GeneralDataService";
import { GoogleMapsModule } from '@angular/google-maps';
import { MailChimpService } from './services/MailChimpService';
import { LocationService } from './services/LocationService';
import { UpdateService } from './services/UpdateService';
import { ServiceWorkerModule } from '@angular/service-worker';
import { PortfolioService } from './services/PortfolioService';
import { TransformationService } from './services/TransformationService';
import { NavigationService } from './services/NavigationService';
import { QuillEditorModule } from './shared/quill-editor.module';

export function initializeApp(appConfig: AppConfig) {
  return () => 
    appConfig.load();
}

const config: InputFileConfig = {
  fileAccept: '*'
};

@NgModule({
  declarations: [
    AppComponent,
    PagesComponent,
    NotFoundComponent,
    UserMenuComponent,
    LangComponent,
    SocialIconsComponent,
    ToolbarComponent,
    HorizontalMenuComponent,
    VerticalMenuComponent
  ],
  imports: [
    BrowserModule, 
    CommonModule,
    FormsModule, 
    GoogleMapsModule,
    NgProgressbar,
    SharedModule,
    AppRoutingModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerImmediately'
    }),
    QuillEditorModule
  ],
  providers: [AppConfig, 
    provideAppInitializer(() => {
      const initializerFn = (initializeApp)(inject(AppConfig));
      return initializerFn();
    }),
    provideAppInitializer(() => {
      const updateService = inject(UpdateService);
      updateService.checkForUpdates();
    }),
    NavigationService, TransformationService, AppSettings, UserManagementApiService, GeneralDataApiService, PropertyDataApiService, AuctionManagementApiService, 
    AuthorizationApiService, SignalRService, AuthenticationService, PropertyService, GeneralDataService, LocationService, MailChimpService, PortfolioService,
    { provide: OverlayContainer, useClass: CustomOverlayContainer },
    // Uses the default fetch HttpBackend. Previously pinned to XHR via withXhr();
    // dropped because the app has no HTTP progress-event consumers. If large
    // uploads or blob downloads regress, restore withXhr().
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: AppInterceptor, multi: true },
    CurrencyPipe, PercentPipe, DatePipe,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent]
})
export class AppModule { }
